'use client'
import styled from 'styled-components'
import { Accordion, AccordionGroup } from '@shipt/design-system-accordion'
import { screenSizes } from '@shipt/design-system-themes'
import { Column, Row } from '@shipt/design-system-layouts'
import { Body } from '@shipt/design-system-typography'
import { AppBadges } from '@/features/shared/components/AppBadges'
import { FooterContent } from '@/features/shared/components/Footers/Footer/FooterContent'
import { NextLink } from '@/features/shared/elements/Link'
import { type FooterSection } from '@/features/shared/components/Footers/types'
import {
  memberSections,
  shopperSections,
} from '@/features/shared/components/Footers/Footer/constants'
import { trackElementClicked } from '@/features/shared/analytics/element'
import { type Hyperlink } from '@/features/shared/constants/hyperlinks'
import { sanitizeHTML } from '@/features/shared/utils/sanitize'
import { GroceriesIcon, VehicleCarIcon } from '@shipt/design-system-icons'
import { ClientOnly } from '@/features/shared/components/ClientOnly'

const FooterHalf = ({
  icon,
  heading,
  isShopper = false,
  sections,
}: {
  icon?: React.ReactNode
  heading: string
  isShopper?: boolean
  sections: FooterSection[]
}) => (
  <Column spacing="lg">
    <Column spacing="md">
      <Row align="center" spacing="sm">
        {icon}
        <Body size="xxl" as="h2" strong>
          {heading}
        </Body>
      </Row>
      {/* ClientOnly, not Suspense because AppBadges calls useSearchParams(), which suspends
          at SSR in the Pages Router. Suspense would stream an inline $RC script that
          our PCI CSP (no 'unsafe-inline') blocks. ClientOnly keeps it off the server completely.
      */}
      <ClientOnly>
        <AppBadges openInNewTab isShopper={isShopper} displaySublocation="footer" />
      </ClientOnly>
    </Column>
    <AccordionGridWrapper>
      <AccordionGroup hasLeadingBorder>
        {sections.map(({ title, links }) => (
          <Accordion key={title} title={title} headerAs="h3" defaultExpanded>
            <Column spacing="xs">
              {links.map((link) => (
                <div key={link.name}>
                  <Body>
                    <LinkToRender link={link} />
                  </Body>
                </div>
              ))}
            </Column>
          </Accordion>
        ))}
      </AccordionGroup>
    </AccordionGridWrapper>
  </Column>
)

const LinkToRender = ({ link: { external, url, name, showUnderline } }: { link: Hyperlink }) => {
  const onClick = () =>
    trackElementClicked(
      {
        type: 'link',
        link_name: name,
        display_format: 'footer',
      },
      { sendImmediately: true },
    )
  if (external) {
    return (
      <ExternalFooterLink onClick={onClick} href={url}>
        {name}
      </ExternalFooterLink>
    )
  }

  return (
    <NextLink href={url} onClick={onClick} prefetch={false} showUnderline={!!showUnderline}>
      {name}
    </NextLink>
  )
}

const FooterSections = () => (
  <Column spacing="3xl">
    <FooterHalf
      icon={<GroceriesIcon />}
      heading="Get Same-Day Delivery with Shipt"
      sections={memberSections}
    />
    <FooterHalf
      icon={<VehicleCarIcon />}
      heading="Get Paid to Shop & Deliver with Shipt"
      isShopper
      sections={shopperSections}
    />
  </Column>
)

type Props = {
  disclaimer?: string
  children?: React.ReactNode
}
export const Footer = ({ disclaimer, children }: Props) => (
  <FooterWrapper id="footer">
    <FooterContent sections={<FooterSections />}>
      {!!disclaimer && (
        <DisclaimerText
          size="md"
          variant="secondary"
          dangerouslySetInnerHTML={{ __html: sanitizeHTML(disclaimer) }}
        />
      )}
      {children}
    </FooterContent>
  </FooterWrapper>
)

const AccordionGridWrapper = styled.div`
  width: 100%;

  @media ${screenSizes.tablet} {
    > div {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 1rem;
    }
  }
`

const FooterWrapper = styled.footer`
  width: 100%;
`

const ExternalFooterLink = styled.a`
  text-decoration: none;
`

const DisclaimerText = styled(Body)`
  display: block;
  margin-top: 1.25rem;
  text-decoration: none;
  white-space: pre-line;

  a {
    line-height: 1.33;
    color: ${({ theme }) => theme.color.content.base.neutral.primary};
    text-decoration: underline;
  }

  b {
    font-weight: 600;
  }

  @media ${screenSizes.tablet} {
    padding: 0;
  }
`
